import { ShieldCheck, Lock, BadgeCheck } from "lucide-react";
import { usePageMeta } from "@/lib/usePageMeta";
import { useLang } from "@/lib/i18n";
import { PageIntro, Reveal } from "@/components/Reveal";

const ICONS = [Lock, ShieldCheck, BadgeCheck];

export default function About() {
  const { t } = useLang();
  const a = t.about;
  usePageMeta(a.meta);

  return (
    <>
      <PageIntro eyebrow={a.eyebrow} title={a.title} subtitle={a.subtitle} testId="about-intro" />

      <section className="border-b border-line" data-testid="about-who">
        <div className="container-x grid gap-8 py-20 md:grid-cols-12 sm:py-28">
          <Reveal className="md:col-span-4">
            <span className="font-mono text-5xl text-coral">01</span>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-navy sm:text-3xl">{a.who.title}</h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7 md:col-start-6">
            <p className="text-base leading-relaxed text-muted md:text-lg">{a.who.text}</p>
            <dl className="mt-10 grid grid-cols-2 gap-6 border-t border-line pt-8 sm:grid-cols-3">
              {a.who.facts.map((f, i) => (
                <div key={f.label}>
                  <dt className="eyebrow">{f.label}</dt>
                  <dd className="mt-2 text-sm font-semibold text-navy" data-testid={["about-form", "about-state", "about-founded"][i]}>
                    {f.value}
                  </dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-line bg-white" data-testid="about-mission">
        <div className="container-x grid gap-8 py-20 md:grid-cols-12 sm:py-28">
          <Reveal className="md:col-span-4">
            <span className="font-mono text-5xl text-coral">02</span>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-navy sm:text-3xl">{a.mission.title}</h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7 md:col-start-6">
            <p className="text-2xl font-medium leading-snug tracking-tight text-brand sm:text-3xl">{a.mission.lead}</p>
            <p className="mt-6 text-base leading-relaxed text-muted md:text-lg">{a.mission.text}</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-how">
        <div className="container-x py-20 sm:py-28">
          <Reveal className="grid gap-8 md:grid-cols-12">
            <div className="md:col-span-4">
              <span className="font-mono text-5xl text-coral">03</span>
              <h2 className="mt-4 text-2xl font-bold tracking-tight text-navy sm:text-3xl">{a.how.title}</h2>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-line bg-line md:grid-cols-3">
            {a.how.principles.map((p, i) => {
              const Icon = ICONS[i];
              return (
                <Reveal
                  key={p.title}
                  delay={i * 0.12}
                  className="group bg-white p-8 transition-colors duration-500 hover:bg-cream sm:p-10"
                  data-testid={`about-principle-${i + 1}`}
                >
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand/[0.08] text-brand transition-transform duration-500 group-hover:-translate-y-1">
                    <Icon size={20} strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-8 text-lg font-bold tracking-tight text-navy">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted md:text-base">{p.text}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
